import { createRouter, createWebHistory } from 'vue-router';

import RouterPlaceholder from '@/components/RouterPlaceholder.vue';
import { Config } from '@/config';
import { Constants } from '@/constants';
import { administratorsOnly, pagesReleased } from '@/helpers/route-guard.helper';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { useAuthStore, useCommunityStore, useConfigurationStore, useUsersStore } from '@/stores';
import CommentModeration from '@/views/CommentModeration.vue';
import CommunitiesView from '@/views/CommunitiesView.vue';
import CommunityListView from '@/views/CommunityListView.vue';
import CustomerOverviewView from '@/views/CustomerOverviewView.vue';
import ConsultationView from '@/views/ConsultationView.vue';
import ConsultationsListView from '@/views/ConsultationsListView.vue';
import DashboardView from '@/views/DashboardView.vue';
import FormDetailView from '@/views/FormDetailView.vue';
import MailingCampaignListView from '@/views/MailingCampaignListView.vue';
import MailingCampaignView from '@/views/MailingCampaignView.vue';
import ModuleDetailView from '@/views/ModuleDetailView.vue';
import PagesListView from '@/views/PagesListView.vue';
import PageView from '@/views/PageView.vue';
import ProjectView from '@/views/ProjectView.vue';
import ProjectsListView from '@/views/ProjectsListView.vue';
import SectionDetailView from '@/views/SectionDetailView.vue';
import EventItemDetailView from '@/views/EventItemDetailView.vue';
import NewsItemDetailView from '@/views/NewsItemDetailView.vue';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: DefaultLayout,
      meta: {
        breadcrumb: {
          label: 'Home',
          icon: 'home',
        },
      },
      children: [
        {
          path: '',
          name: 'home',
          component: DashboardView,
        },
        {
          path: 'consultations',
          component: RouterPlaceholder,
          meta: {
            breadcrumb: {
              label: 'Consultations',
            },
          },
          children: [
            {
              path: '',
              name: 'consultations_list',
              component: ConsultationsListView,
            },
            {
              path: '/consultations/:id',
              name: 'consultation_details',
              component: ConsultationView,
              props: {
                isTemplate: false,
              },
              meta: {
                breadcrumb: {
                  label: (route: any) => {},
                },
              },
            },
          ],
        },
        {
          path: '/sections/:id',
          name: 'section_details',
          component: SectionDetailView,
          props: {
            isTemplate: false,
          },
        },
        {
          path: '/forms/:id',
          name: 'form_details',
          component: FormDetailView,
          props: {
            isTemplate: false,
          },
        },
        {
          path: '/consultation-templates/:id',
          name: 'consultation_template_details',
          component: ConsultationView,
          props: {
            isTemplate: true,
          },
        },
        {
          path: '/consultation-templates/:consultationId/sections/:id',
          name: 'section_template_details',
          component: SectionDetailView,
          props: {
            isTemplate: true,
          },
        },
        {
          path: '/consultation-templates/:consultationId/sections/:sectionId/forms/:id',
          name: 'form_template_details',
          component: FormDetailView,
          props: {
            isTemplate: true,
          },
        },
        {
          path: 'projects/:projectId/modules/:moduleId',
          name: 'module_details',
          component: ModuleDetailView,
          props: {
            parentType: Constants.Block.ParentType.Project,
            parentRouteName: 'project_details',
            parentIcon: 'o_folder_copy',
          },
        },
        {
          path: 'projects/:projectId/modules/:moduleId/event-items/:id',
          name: 'event_item_details',
          component: EventItemDetailView,
        },
        {
          path: 'projects/:projectId/modules/:moduleId/news-items/:id',
          name: 'news_item_details',
          component: NewsItemDetailView,
        },
        {
          path: 'project-templates/:projectId/modules/:moduleId',
          name: 'template_module_details',
          component: ModuleDetailView,
          props: {
            parentType: Constants.Block.ParentType.Project,
            parentRouteName: 'project_template_details',
            parentIcon: 'o_settings',
          },
        },
        {
          path: 'pages/:pageId/blocks/:moduleId',
          name: 'page_block_details',
          component: ModuleDetailView,
          props: {
            parentType: Constants.Block.ParentType.Page,
            parentRouteName: 'page_details',
            parentIcon: 'o_article',
          },
          beforeEnter: [administratorsOnly, pagesReleased],
        },
        {
          path: '/pages',
          name: 'pages_list',
          component: PagesListView,
          beforeEnter: [administratorsOnly, pagesReleased],
        },
        {
          path: '/pages/:id',
          name: 'page_details',
          component: PageView,
          beforeEnter: [administratorsOnly, pagesReleased],
        },
        {
          path: '/communities/',
          name: 'communities',
          component: CommunityListView,
        },
        {
          path: '/customers',
          name: 'customers_overview',
          component: CustomerOverviewView,
          beforeEnter: (_to, _from, next) => {
            // Superadmin-only (backend also enforces this on /admin/customers-overview).
            if (useUsersStore().currentUser?.isSuperadmin) {
              next();
            } else {
              next('/');
            }
          },
        },
        {
          path: '/settings',
          name: 'community_details',
          component: CommunitiesView,
        },
        {
          path: '/comment-moderation/',
          name: 'comment-moderation',
          component: CommentModeration,
        },
        {
          path: 'projects',
          component: RouterPlaceholder,
          meta: {
            breadcrumb: {
              label: 'Projects',
            },
          },
          children: [
            {
              path: '',
              name: 'projects_list',
              component: ProjectsListView,
            },
            {
              path: '/projects/:id',
              name: 'project_details',
              component: ProjectView,
              props: {
                isTemplate: false,
              },
              meta: {
                breadcrumb: {
                  label: (route: any) => {},
                },
              },
            },
            {
              path: '/project-templates/:id',
              name: 'project_template_details',
              component: ProjectView,
              props: {
                isTemplate: true,
              },
            },
          ],
        },
        {
          path: 'mailing-campaign',
          component: RouterPlaceholder,
          meta: {
            breadcrumb: {
              label: 'campaign',
            },
          },
          children: [
            {
              path: '',
              name: 'mailingCampaignList',
              component: MailingCampaignListView,
            },
            {
              path: '/mailing-campaign/:id',
              name: 'mailing_campaign_detail',
              component: MailingCampaignView,
              meta: {
                breadcrumb: {
                  label: (route: any) => {},
                },
              },
            },
          ],
        },
      ],
    },
    {
      path: '/login',
      component: { template: '<div></div>' },
      beforeEnter: async (to, from, next) => {
        const token = to.query.token as string;
        const auth = useAuthStore();
        try {
          await auth.login(token);
          await useConfigurationStore().fetchConfigurations();
          await useCommunityStore().fetchCommunities();
          next('/');
        } catch (err) {
          next(false);
          auth.logout();
        }
      },
    },
    {
      path: '/login/:communitySlug',
      component: {},
      beforeEnter: (_to, _from, next) => next('/'),
    },
    {
      path: '/communities/:id',
      name: 'communities_detail',
      component: {},
      beforeEnter: (_to, _from, next) => next('/'),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  // redirect to login page if not logged in and trying to access a restricted page
  const publicPages = ['/login'];
  const authRequired = !publicPages.includes(to.path);
  const authStore = useAuthStore();
  const communityStore = useCommunityStore();
  const needToSwitchCommunity = to.name === 'communities_detail' && to.params.id;
  if (needToSwitchCommunity) {
    const communityId = parseInt(to.params.id as string);
    communityStore.setLastCommunity(communityId);
  }
  if (authRequired && !authStore.user) {
    next(false);
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set('idpHint', 'admin');
    urlSearchParams.set('redirectUri', `${window.location.protocol}//${window.location.host}/login`);
    if (to.params?.communitySlug) {
      urlSearchParams.set('communitySlug', to.params.communitySlug.toString());
    }
    window.location.href = `${Config.accounts.url}/login?${urlSearchParams.toString()}`;
  } else if (needToSwitchCommunity) {
    const communityId = parseInt(to.params.id as string);
    // A superadmin who gives up entering a customer community stays where they were.
    next((await communityStore.switchCommunity({ id: communityId } as any)) ? undefined : false);
  } else {
    next();
  }
});

export default router;
